import { createTokenHeader } from '../utils/functions/headers';
import {
  TDeliverySchedule,
  TDeliverySchedulesImportResponse,
  TExportDeliveryScheduleParams,
  TMutationGeneralResponse,
  TOrderStatusCount,
  TUpdateDeliveryScheduleData,
  TgetDeliveryScheduleParams,
} from '../utils/types';
import { emptySplitApi } from './emptySplitApi';

export const deliverySchedulesService = emptySplitApi
  .enhanceEndpoints({
    addTagTypes: [
      'DeliverySchedules',
      'DeliverySchedulesCount',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getDeliverySchedules: builder.query<
        { data: TDeliverySchedule[]; totalCount: number },
        TgetDeliveryScheduleParams
      >({
        query: (params) => ({
          method: 'GET',
          url: 'delivery-schedules',
          params: {
            page: params.pageNo,
            size: params.pageSize,
            searchKey: params.searchString
              ? params.searchString
              : null,
            plants: params.plant,
            status: params.orderStatus,
            transporterNames: params.vehicleNumber,
            lrNumbers: params.lrNumber,
            plantRegionList:
              params.plantRegionList?.join(',') || null,
            plantModelList:
              params.plantModelList?.join(',') || null,
          },
        }),
        transformResponse: (
          data: TDeliverySchedule[],
          meta: Response
        ) => {
          const totalCount =
            meta.headers.get('X-total-count');
          return {
            data,
            totalCount: Number(totalCount ? totalCount : 0),
          };
        },
        providesTags: ['DeliverySchedules'],
      }),
      getOrderStatusCount: builder.query<
        TOrderStatusCount[],
        void
      >({
        query: () => ({
          method: 'GET',
          url: 'delivery-schedules/all-status/count',
        }),
        providesTags: ['DeliverySchedulesCount'],
      }),
      deliverySchedulesImport: builder.mutation<
        TDeliverySchedulesImportResponse,
        FormData
      >({
        query: (data) => {
          return {
            url: 'delivery-schedules/import',
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
        invalidatesTags: [
          'DeliverySchedules',
          'DeliverySchedulesCount',
        ],
      }),
      orderDeliveredStatusImport: builder.mutation<
        TDeliverySchedulesImportResponse,
        FormData
      >({
        query: (data) => {
          return {
            url: 'retailer/order/delivery/import',
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
        invalidatesTags: [
          'DeliverySchedules',
          'DeliverySchedulesCount',
        ],
      }),
      markOrdersAsDispatch: builder.mutation<
        { message: string },
        number[]
      >({
        query: (data) => {
          return {
            url: 'retailer/sales/orders/mark/rtd?status=dispatched',
            method: 'POST',
            data,
          };
        },
        invalidatesTags: [
          'DeliverySchedules',
          'DeliverySchedulesCount',
        ],
      }),
      exportDeliverySchedules: builder.query<
        { url?: string },
        TExportDeliveryScheduleParams
      >({
        query: (data) => {
          return {
            url: 'delivery-schedules/export',
            method: 'POST',
            data,
          };
        },
      }),
      updateDeliverySchedule: builder.mutation<
        TMutationGeneralResponse,
        TUpdateDeliveryScheduleData
      >({
        query: (data) => {
          return {
            url: 'delivery-schedules',
            method: 'PUT',
            data,
          };
        },
        invalidatesTags: [
          'DeliverySchedules',
          'DeliverySchedulesCount',
        ],
      }),
    }),
  });

export const {
  useGetDeliverySchedulesQuery,
  useGetOrderStatusCountQuery,
  useDeliverySchedulesImportMutation,
  useOrderDeliveredStatusImportMutation,
  useMarkOrdersAsDispatchMutation,
  useLazyExportDeliverySchedulesQuery,
  useUpdateDeliveryScheduleMutation,
} = deliverySchedulesService;
